import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import LandingPage from "@/pages/LandingPage";
import { Toaster } from "@/components/ui/sonner";
import { AppAuthProvider } from "@/context/AppAuthContext";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { useParams } from "react-router-dom";

const B2ProJobRedirect = () => {
  const { jobId } = useParams();
  return <Navigate to={`/b2/pro/jobs/${jobId}`} replace />;
};

const AdminPage = lazy(() => import("@/pages/AdminPage"));

const B2Home = lazy(() => import("@/b2/pages/B2Home"));
const B2RequestFlow = lazy(() => import("@/b2/pages/B2RequestFlow"));
const B2Finding = lazy(() => import("@/b2/pages/B2Finding"));
const B2Responses = lazy(() => import("@/b2/pages/B2Responses"));
const B2Opportunity = lazy(() => import("@/b2/pages/B2Opportunity"));
const B2QuoteJoin = lazy(() => import("@/b2/pages/B2QuoteJoin"));
const B2AppHome = lazy(() => import("@/b2/app/B2AppHome"));
const B2AppRequests = lazy(() => import("@/b2/app/B2AppRequests"));
const B2AppMessages = lazy(() => import("@/b2/app/B2AppMessages"));
const B2AppRewards = lazy(() => import("@/b2/app/B2AppRewards"));
const B2AppAccount = lazy(() => import("@/b2/app/B2AppAccount"));
const B2Login = lazy(() => import("@/b2/pages/B2Login"));
const B2Review = lazy(() => import("@/b2/pages/B2Review"));
const B2ProviderPublic = lazy(() => import("@/b2/pages/B2ProviderPublic"));
const B2AppThread = lazy(() => import("@/b2/app/B2AppThread"));
const B2AppProfile = lazy(() => import("@/b2/app/B2AppProfile"));
const B2ProHome = lazy(() => import("@/b2/pro/B2ProHome"));
const B2ProOpportunities = lazy(() => import("@/b2/pro/B2ProOpportunities"));
const B2ProJobs = lazy(() => import("@/b2/pro/B2ProJobs"));
const B2ProJob = lazy(() => import("@/b2/pro/B2ProJob"));
const B2ProMessages = lazy(() => import("@/b2/pro/B2ProMessages"));
const B2ProProfile = lazy(() => import("@/b2/pro/B2ProProfile"));
const B2ProServices = lazy(() => import("@/b2/pro/B2ProServices"));
const B2ProJoin = lazy(() => import("@/b2/pro/B2ProJoin"));

const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"));
const DemoCenter = lazy(() => import("@/pages/DemoCenter"));
const DemoMode = lazy(() => import("@/pages/DemoMode"));
const EstimateFlow = lazy(() => import("@/pages/EstimateFlow"));
const LocalHub = lazy(() => import("@/pages/LocalHub"));
const AppHome = lazy(() => import("@/pages/app/AppHome"));
const BuildingArea = lazy(() => import("@/pages/app/BuildingArea")); // eslint-disable-line no-unused-vars
const AppAuth = lazy(() => import("@/pages/app/AppAuth"));
const ResetPassword = lazy(() => import("@/pages/app/ResetPassword"));
const GetHelp = lazy(() => import("@/pages/app/GetHelp"));
const EarnMoney = lazy(() => import("@/pages/app/EarnMoney"));
const JobDetail = lazy(() => import("@/pages/app/JobDetail"));
const OpenRequests = lazy(() => import("@/pages/app/OpenRequests"));
const Workspace = lazy(() => import("@/pages/app/Workspace"));
const MyProjects = lazy(() => import("@/pages/app/MyProjects"));
const SavedProviders = lazy(() => import("@/pages/app/SavedProviders"));
const MyReviews = lazy(() => import("@/pages/app/MyReviews"));
const Rewards = lazy(() => import("@/pages/app/Rewards"));
const Messages = lazy(() => import("@/pages/app/Messages"));
const Profile = lazy(() => import("@/pages/app/Profile"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const BrowseProviders = lazy(() => import("@/pages/app/BrowseProviders"));
const ProviderDashboard = lazy(() => import("@/pages/app/ProviderDashboard"));
const ProviderLeads = lazy(() => import("@/pages/app/ProviderLeads"));
const SendQuote = lazy(() => import("@/pages/app/SendQuote"));
const ProviderQuotes = lazy(() => import("@/pages/app/ProviderQuotes"));
const ProviderPipeline = lazy(() => import("@/pages/app/ProviderPipeline"));
const Featured = lazy(() => import("@/pages/app/Featured"));
const Notifications = lazy(() => import("@/pages/app/Notifications"));
const TermsSafetyPage = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.TermsSafetyPage })));
const ProviderAgreementPage = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.ProviderAgreementPage })));
const SafetyPage = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.SafetyPage })));
const PrivacyPolicyPage = lazy(() => import("@/pages/LegalDocs").then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import("@/pages/LegalDocs").then((m) => ({ default: m.TermsOfServicePage })));
const RewardsTermsPage = lazy(() => import("@/pages/LegalDocs").then((m) => ({ default: m.RewardsTermsPage })));
const CookiePolicyPage = lazy(() => import("@/pages/LegalDocs").then((m) => ({ default: m.CookiePolicyPage })));
const CommunityGuidelinesPage = lazy(() => import("@/pages/LegalDocs").then((m) => ({ default: m.CommunityGuidelinesPage })));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const ComingLater = lazy(() => import("@/pages/ComingLater"));
const SearchResults = lazy(() => import("@/pages/SearchResults"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const JunkRemovalPage = lazy(() => import("@/pages/JunkRemovalPage"));
const ForProvidersPage = lazy(() => import("@/pages/ForProvidersPage"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const AreasPage = lazy(() => import("@/pages/AreasPage")); // eslint-disable-line no-unused-vars
const PublicStub = lazy(() => import("@/components/brivio/public/PublicShell").then((m) => ({ default: m.PublicStub })));

const MY_TOWN_ENABLED = process.env.REACT_APP_MY_TOWN_ENABLED === "true";

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="route-loading">
    <Loader2 className="w-7 h-7 animate-spin text-[#2563EB]" />
  </div>
);

// Fires a GTM page_view on every SPA route change (path + search).
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppAuthProvider>
          <RouteTracker />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/b2" element={<B2Home />} />
            <Route path="/b2/request" element={<B2RequestFlow />} />
            <Route path="/b2/finding" element={<B2Finding />} />
            <Route path="/b2/responses" element={<B2Responses />} />
            <Route path="/b2/opportunity" element={<B2Opportunity />} />
            <Route path="/b2/quote" element={<B2QuoteJoin />} />
            <Route path="/b2/app" element={<B2AppHome />} />
            <Route path="/b2/app/requests" element={<B2AppRequests />} />
            <Route path="/b2/app/messages" element={<B2AppMessages />} />
            <Route path="/b2/app/rewards" element={<B2AppRewards />} />
            <Route path="/b2/app/account" element={<B2AppAccount />} />
            <Route path="/b2/login" element={<B2Login />} />
            <Route path="/b2/review" element={<B2Review />} />
            <Route path="/b2/provider/:providerId" element={<B2ProviderPublic />} />
            <Route path="/b2/app/messages/:wsId" element={<B2AppThread />} />
            <Route path="/b2/app/profile" element={<B2AppProfile />} />
            <Route path="/b2/pro" element={<B2ProHome />} />
            <Route path="/b2/pro/opportunities" element={<B2ProOpportunities />} />
            <Route path="/b2/pro/jobs" element={<B2ProJobs />} />
            <Route path="/b2/pro/jobs/:jobId" element={<B2ProJob />} />
            <Route path="/b2/pro/quotes" element={<Navigate to="/b2/pro/jobs" replace />} />
            <Route path="/b2/pro/job/:jobId" element={<B2ProJobRedirect />} />
            <Route path="/b2/pro/messages" element={<B2ProMessages />} />
            <Route path="/b2/pro/profile" element={<B2ProProfile />} />
            <Route path="/b2/pro/services" element={<B2ProServices />} />
            <Route path="/b2/pro/join" element={<B2ProJoin />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/junk-removal" element={<JunkRemovalPage />} />
            <Route path="/providers" element={<ForProvidersPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/areas" element={<Navigate to="/search" replace />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/app/plans" element={<PlansPage />} />
            <Route path="/p/:id" element={<PublicProfile />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/founding" element={<Navigate to="/app/auth?mode=signup&role=provider" replace />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/demo" element={<DemoCenter />} />
            <Route path="/demo-mode" element={<DemoMode />} />
            <Route path="/estimate" element={<Navigate to="/app/get-help" replace />} />
            <Route path="/local" element={MY_TOWN_ENABLED ? <LocalHub /> : <ComingLater />} />
            <Route path="/my-town" element={MY_TOWN_ENABLED ? <Navigate to="/local" replace /> : <ComingLater />} />
            <Route path="/explore" element={<ComingLater />} />
            <Route path="/categories" element={<ComingLater />} />
            <Route path="/request-category" element={<ComingLater />} />
            <Route path="/app/my-town" element={<ComingLater backTo="/app" backLabel="Back to app" />} />
            <Route path="/app" element={<AppHome />} />
            <Route path="/app/building" element={<Navigate to="/app" replace />} />
            <Route path="/app/auth" element={<AppAuth />} />
            <Route path="/app/reset-password" element={<ResetPassword />} />
            <Route path="/app/get-help" element={<GetHelp />} />
            <Route path="/app/dashboard" element={<ProviderDashboard />} />
            <Route path="/app/leads" element={<ProviderLeads />} />
            <Route path="/app/leads/:id/quote" element={<SendQuote />} />
            <Route path="/app/quotes" element={<ProviderQuotes />} />
            <Route path="/app/pipeline" element={<ProviderPipeline />} />
            <Route path="/app/earn" element={<EarnMoney />} />
            <Route path="/app/providers" element={<BrowseProviders />} />
            <Route path="/app/featured" element={<Featured />} />
            <Route path="/app/community" element={<Navigate to="/app" replace />} />
            <Route path="/app/messages" element={<Messages />} />
            <Route path="/app/profile" element={<Profile />} />
            <Route path="/app/notifications" element={<Notifications />} />
            <Route path="/app/jobs/:id" element={<JobDetail />} />
            <Route path="/app/requests" element={<OpenRequests />} />
            <Route path="/app/workspace/:jobId" element={<Workspace />} />
            <Route path="/app/projects" element={<MyProjects />} />
            <Route path="/app/saved" element={<SavedProviders />} />
            <Route path="/app/reviews" element={<MyReviews />} />
            <Route path="/app/rewards" element={process.env.REACT_APP_REWARDS_ENABLED === "true" ? <Rewards /> : <Navigate to="/app" replace />} />
            <Route path="/terms" element={<TermsSafetyPage />} />
            <Route path="/app/terms" element={<TermsSafetyPage />} />
            <Route path="/provider-agreement" element={<ProviderAgreementPage />} />
            <Route path="/app/provider-agreement" element={<ProviderAgreementPage />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/app/safety" element={<SafetyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/app/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/app/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/app/cookies" element={<CookiePolicyPage />} />
            <Route path="/rewards-terms" element={<RewardsTermsPage />} />
            <Route path="/app/rewards-terms" element={<RewardsTermsPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/app/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/app/contact" element={<ContactPage />} />
          </Routes>
          </Suspense>
        </AppAuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
